import React, { useEffect } from 'react';
import a11yIcewormService from '../../services/a11yIcewormService';

/**
 * Helper to determine the target container for the focus trap.
 * * Strategy:
 * 1. If a specific element (ref) is provided, use that (New, specific behavior).
 * 2. If not, fall back to 'main-screen-content' or document.body (Legacy, aggressive behavior).
 */
const getTrapTarget = (specificElement) => {
    if (specificElement) return specificElement;

    const mainScreenContent = document.getElementById('main-screen-content');
    return mainScreenContent || document.body;
};

/**
 * Higher-Order Component (HOC) for wrapping legacy Class Components with focus trap functionality.
 * * Enables Iceworm toolbar integration for IIB (Inspera Integrated Browser) screen reader support.
 * * Used for legacy Class Components (e.g., OptionsWrapper) that cannot use hooks.
 * * Maintains backward compatibility by defaulting to the global 'main-screen-content' trap.
 * * Manages focus handoff between the test player and Iceworm toolbar.
 */
export function withIcewormFocusTrap(WrappedComponent) {
    class WithIcewormFocusTrap extends React.Component {
        componentDidMount() {
            if (a11yIcewormService.isIcewormHandoffEnabled()) {
                // No specific ref available in HOC usage, so it uses the fallback
                this.trapContainer = getTrapTarget();
                a11yIcewormService.addFocusTrap(this.trapContainer);
            }
        }

        componentWillUnmount() {
            if (this.trapContainer) {
                a11yIcewormService.removeFocusTrap(this.trapContainer);
            }
        }

        render() {
            return <WrappedComponent {...this.props} />;
        }
    }
    WithIcewormFocusTrap.displayName = `WithIcewormFocusTrap(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
    return WithIcewormFocusTrap;
}

/**
 * React Hook
 * * @param {React.RefObject} [elementRef] - Optional ref to a specific DOM element.
 * - If provided: Traps focus ONLY within that element.
 * - If omitted: Falls back to trapping 'main-screen-content' (Backward Compatibility).
 */
export function useIcewormFocusTrap(elementRef) {
    useEffect(() => {
        // Check feature flag support and IEP context
        if (!a11yIcewormService.isIcewormHandoffEnabled()) {
            return undefined;
        }

        // Determine target: use the specific ref if passed, otherwise fallback to global
        const trapContainer = getTrapTarget(elementRef?.current);

        if (trapContainer) {
            a11yIcewormService.addFocusTrap(trapContainer);
        }

        return () => {
            if (trapContainer) {
                a11yIcewormService.removeFocusTrap(trapContainer);
            }
        };
    }, [elementRef]);
}
